import type { PushProviderId } from '@/config/push'

export type { PushProviderId }

export type PushPermissionStatus = 'granted' | 'denied' | 'undetermined'

/**
 * Who the user is, in terms every provider can consume.
 *
 * `auth0Id` is the canonical external id. OneSignal historically logged in with
 * the Mongo id, so `legacyUserId` keeps that stable rather than re-keying every
 * existing OneSignal profile mid-migration.
 */
export type PushIdentity = {
  auth0Id: string
  legacyUserId?: string
  email?: string
  name?: string
  traits?: Record<string, string | number | boolean | undefined>
}

/** A notification the user tapped, normalized across providers. */
export type PushClickEvent = {
  provider: PushProviderId
  /**
   * Raw deep link exactly as the campaign authored it — a router path, a
   * `pokerpower://` URL, or an https link. Hand it to `resolveDeepLink`.
   */
  link?: string
}

export type Unsubscribe = () => void

export type PushProviderContext = {
  /** True for the one provider that registers with APNs/FCM and prompts for permission. */
  ownsDeviceToken: boolean
}

/**
 * The contract every push SDK is wrapped in.
 *
 * Nothing outside `src/module/push` may import `react-native-onesignal` or
 * `customerio-reactnative` directly — that is what makes removing a provider a
 * matter of deleting one file and dropping it from `EXPO_PUBLIC_PUSH_PROVIDERS`.
 */
export interface PushProvider {
  readonly id: PushProviderId

  initialize(context: PushProviderContext): Promise<void>

  identify(identity: PushIdentity): Promise<void>
  clearIdentity(): Promise<void>
  /** Free-form profile/user attributes. Providers that have no equivalent skip it. */
  setAttributes?(attributes: Record<string, string>): Promise<void>

  getPermissionStatus(): Promise<PushPermissionStatus>
  /** Only ever called on the token owner. */
  requestPermission?(options: { fallbackToSettings: boolean }): Promise<void>
  /** Only ever called on the token owner. */
  optIn?(): Promise<void>

  /** Read the APNs/FCM token. Implemented by providers that can own it. */
  getDeviceToken?(): Promise<string | undefined>
  /** Take a token fetched by the owning provider. */
  acceptDeviceToken?(token: string): Promise<void>
  /** Fires when the owning provider's token changes, so the facade can fan it out. */
  addTokenListener?(listener: (token: string) => void): Unsubscribe

  addClickListener(listener: (event: PushClickEvent) => void): Unsubscribe
  addPermissionListener?(listener: (granted: boolean) => void): Unsubscribe
}
